import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';
import * as Device from 'expo-device';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useAuthStore, useChatStore, useChatInputStore, useToastStore } from '@/store';
import { chatsApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import { focusChatInput } from '@/lib/chatInputFocus';
import { SideChatContext } from './SideChatContext';

export default function SideChatLayout({ renderChat, ...props }) {
  const id = Array.isArray(props.conversationId) ? props.conversationId[0] : props.conversationId;
  const chat = useChatStore(state => state.chats.find(item => item.id === id)) || props.chat;
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const { isLG } = useResponsiveness();
  const { colors } = useTheme();
  const router = useRouter();
  const [sideChat, setSideChat] = useState(null);
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(0);
  const busyRef = useRef(false);
  const sourceRef = useRef(id);
  sourceRef.current = id;
  const desktop = Platform.OS === 'web' && isLG
    && Device.deviceType !== Device.DeviceType.PHONE && Device.deviceType !== Device.DeviceType.TABLET;
  const enabled = desktop && !props.isCompact && chat?.isOwner && !isGhost;

  const close = useCallback(() => {
    requestRef.current += 1;
    busyRef.current = false;
    setBusy(false);
    setSideChat(null);
    useChatInputStore.getState().setContext(id);
  }, [id]);

  useEffect(() => {
    if (sideChat && desktop) focusChatInput(sideChat.id);
  }, [sideChat, desktop]);

  useEffect(() => {
    setSideChat(null);
    setBusy(false);
    busyRef.current = false;
    return () => { requestRef.current += 1; };
  }, [id]);

  const ask = useCallback(async ({ conversationId, messageId, text }) => {
    if (!enabled || busyRef.current || conversationId !== id || !text?.trim()) return;
    const source = useChatStore.getState().chats.find(item => item.id === id) || chat;
    const message = source?.messages?.find(item => item.id === messageId);
    if (!message || message.isArchivedMessage) return;
    const request = ++requestRef.current;
    busyRef.current = true;
    setBusy(true);
    try {
      const fork = await chatsApi.forkConversation(id, messageId);
      useChatStore.getState().addChat(fork);
      if (request !== requestRef.current || sourceRef.current !== id) return;
      const input = useChatInputStore.getState();
      input.setContext(fork.id);
      input.setQuote(text.trim());
      setSideChat(fork);
    } catch (error) {
      if (request === requestRef.current) {
        useToastStore.getState().actions.addToast(error.message || 'Could not open side chat', 'error');
      }
    } finally {
      if (request === requestRef.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }, [enabled, id, chat]);

  const showSide = desktop && (sideChat || busy);
  return (
    <View style={{ flex: 1, minWidth: 0, flexDirection: 'row' }}>
      <View style={{ flex: 1, minWidth: 0 }}>
        <SideChatContext.Provider value={enabled ? { ask, busy } : null}>
          {renderChat({ ...props, isCompact: props.isCompact || !!showSide })}
        </SideChatContext.Provider>
      </View>
      {showSide && (
        <View testID="side-chat-panel" style={{ flex: 1, minWidth: 0, borderLeftWidth: 1, borderLeftColor: colors.border_color_secondary }}>
          <View style={{ padding: 14, gap: 12, flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <Text weight="medium">Side chat</Text>
              <Text size="xs" color="secondary">Saved as a separate conversation</Text>
            </View>
            {sideChat && <Pressable accessibilityRole="link" accessibilityLabel="Open side chat as full conversation" onPress={() => router.push(`/c/${sideChat.id}`)}>
              <Text size="sm" color="secondary">Open chat</Text>
            </Pressable>}
            <Pressable accessibilityRole="button" accessibilityLabel="Close side chat" onPress={close} hitSlop={8}>
              <Text size="sm">Close</Text>
            </Pressable>
          </View>
          <SideChatContext.Provider value={null}>
            {sideChat ? renderChat({ conversationId: sideChat.id, chat: sideChat, isCompact: true }) : (
              <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Text color="secondary">Creating side chat…</Text>
              </View>
            )}
          </SideChatContext.Provider>
        </View>
      )}
    </View>
  );
}
